import type { SessionView } from '~/helpers/learner/session-view'
import { Search } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '~/components/ui/input'
import { searchLearnerCatalog } from '~/helpers/learner/learner-search'
import { useActiveOption } from '~/hooks/use-active-option'
import { useListSearch } from '~/hooks/use-list-search'
import { cn } from '~/lib/utils'
import { optionId } from '~/pages/scorm/components/learner-search/option-id'
import SearchResults from '~/pages/scorm/components/learner-search/SearchResults'

/** Recherche locale, sur des données déjà chargées : on peut répondre plus vite que les listes du back-office. */
const LEARNER_SEARCH_DELAY_MS = 250

type IProps = {
  sessions: SessionView[]
  onSelect: (target: string) => void
  inputRef?: React.Ref<HTMLInputElement>
  autoFocus?: boolean
  /** À droite du champ : l'indice du raccourci, sur ordinateur. */
  hint?: React.ReactNode
  /** Raccourcis qui donnent le focus au champ, pour les lecteurs d'écran (`aria-keyshortcuts`). */
  keyShortcuts?: string
  /** Placement du panneau : flottant sous le champ, ou dans le flux (plein écran mobile). */
  panelClassName?: string
}

/**
 * Champ de recherche apprenant et ses résultats, en combobox ARIA (#109) : le
 * focus reste dans le champ, l'option active est désignée par
 * `aria-activedescendant`. Flèches pour parcourir, Entrée pour ouvrir, Échap
 * pour fermer le panneau.
 */
function SearchCombobox({ sessions, onSelect, inputRef, autoFocus, hint, keyShortcuts, panelClassName }: IProps) {
  const { t } = useTranslation()
  const listboxId = `${useId()}-results`
  const { search, setSearch, query } = useListSearch(undefined, LEARNER_SEARCH_DELAY_MS)
  const [open, setOpen] = useState(false)

  const results = useMemo(() => searchLearnerCatalog(sessions, query), [sessions, query])
  const targets = [...results.sessions, ...results.modules].map(hit => hit.target)
  const { activeIndex, move, setActiveIndex } = useActiveOption(targets.length, query)
  const showPanel = open && query !== ''
  const expanded = showPanel && targets.length > 0
  // Annoncé par une région toujours présente : une région live insérée en même
  // temps que son texte n'est, le plus souvent, pas lue.
  const announcement = !showPanel
    ? ''
    : expanded ? t('learnerSearch.count', { count: targets.length }) : t('common.noResultsFor', { query })

  const select = (target: string) => {
    onSelect(target)
    setSearch('')
    setOpen(false)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = targets[activeIndex]
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      // Panneau fermé : la flèche le rouvre sur le premier résultat, sans le sauter.
      if (expanded) move(event.key === 'ArrowDown' ? 1 : -1)
      else setOpen(true)
    } else if (event.key === 'Enter' && expanded && target) {
      event.preventDefault()
      select(target)
    } else if (event.key === 'Escape' && showPanel) {
      // Ferme le panneau sans quitter le champ. Dans le dialogue mobile, Radix
      // intercepte Échap avant nous (phase de capture) et ferme le dialogue.
      event.preventDefault()
      setOpen(false)
    }
  }

  return (
    // `onBlur` délégué : il ferme le panneau quand le focus quitte le bloc, il
    // n'en fait pas un élément interactif.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="relative w-full"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false)
      }}
    >
      {/* Icône et indice centrés sur le CHAMP, pas sur le bloc entier : sur
          mobile, le panneau est dans le flux et l'allonge — la loupe
          descendait au milieu des résultats. */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          ref={inputRef}
          role="combobox"
          aria-label={t('learnerSearch.label')}
          aria-expanded={expanded}
          aria-controls={expanded ? listboxId : undefined}
          aria-keyshortcuts={keyShortcuts}
          aria-autocomplete="list"
          aria-activedescendant={expanded && activeIndex >= 0 ? optionId(listboxId, activeIndex) : undefined}
          placeholder={t('learnerSearch.placeholder')}
          // Focus voulu dans le panneau de recherche que l'utilisateur vient d'ouvrir.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="bg-background pl-9 pr-10 text-foreground"
        />
        {hint && <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">{hint}</span>}
      </div>
      {showPanel && (
        // Un clic dans le panneau — barre de défilement, titre de groupe — ne doit
        // pas retirer le focus du champ : le panneau se fermerait au `blur`.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={cn('rounded-xl border bg-popover p-2 text-popover-foreground', panelClassName)}
          onMouseDown={event => event.preventDefault()}
        >
          <SearchResults
            listboxId={listboxId}
            results={results}
            query={query}
            activeIndex={activeIndex}
            onSelect={select}
            onHover={setActiveIndex}
          />
        </div>
      )}
      <p role="status" className="sr-only">{announcement}</p>
    </div>
  )
}

export default SearchCombobox
